import React, { useEffect, useMemo, useRef, useState } from 'react';
import { App, Avatar, Button, FloatButton, Input, InputNumber, Modal, Space, Tag, Typography } from 'antd';
import { RobotOutlined, SendOutlined, UserOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  callOnboardingCopilot,
  type OnboardingCopilotOption,
  type OnboardingCopilotInputRequest,
} from '@/api/onboardingCopilot';
import { useAuth } from '@/contexts/AuthContext';
import { useOnboardingFlow } from '@/contexts/OnboardingFlowContext';
import i18n from '@/i18n';

const { Text, Paragraph } = Typography;

type CopilotMessage = {
  id: string;
  role: 'assistant' | 'user';
  content: string;
  ts: number;
  options?: OnboardingCopilotOption[];
  inputRequests?: OnboardingCopilotInputRequest[];
};

const STORAGE_KEY = 'myapartment_copilot_messages_v1';
const MAX_LOCAL_MESSAGES = 50;

type StoredOnboardingContext = {
  step: string;
  progress: number;
  stepLabel: string;
  stepPrompt: string;
  updatedAt?: number;
};

const STEP_LABEL: Record<string, string> = {
  blocks: 'Block planning',
  'individual-blocks': 'Create blocks',
  'bulk-blocks': 'Bulk upload blocks',
  residents: 'Assign residents',
  'bulk-residents': 'Bulk upload residents',
  invitation: 'Invite residents',
  complete: 'Completed',
};

const STEP_PROMPT: Record<string, string> = {
  blocks:
    'How many blocks do you have? If it is 5+ or 10+, bulk upload is usually faster.',
  'individual-blocks':
    'Now provide floors and units for each block so we can proceed to residents.',
  'bulk-blocks':
    'Upload the blocks CSV to continue to resident onboarding.',
  residents:
    'Assign residents to units. You can continue even if not all residents are added.',
  'bulk-residents':
    'Upload residents in bulk CSV and then continue to invitations.',
  invitation:
    'Send invitations so residents can join. Start with a small batch first.',
  complete:
    'Onboarding is complete.',
};


function loadMessages(): CopilotMessage[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((m) => m && typeof m === 'object')
      .map((m) => m as Partial<CopilotMessage>)
      .filter(
        (m): m is CopilotMessage =>
          (m.role === 'assistant' || m.role === 'user') &&
          typeof m.content === 'string' &&
          typeof m.id === 'string' &&
          typeof m.ts === 'number'
      )
      .map((m) => ({
        ...m,
        options: Array.isArray(m.options) ? m.options : undefined,
        inputRequests: Array.isArray((m as any).inputRequests) ?
          (m as any).inputRequests :
          undefined,
      }))
      .slice(-MAX_LOCAL_MESSAGES);
  } catch {
    return [];
  }
}

function saveMessages(messages: CopilotMessage[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(messages.slice(-MAX_LOCAL_MESSAGES)));
  } catch {
    // ignore quota / private mode issues
  }
}

function buildPreviewBlockNames(flow: Record<string, unknown>): string[] {
  const blockCountRaw = flow.blockCount;
  const blockCount =
    typeof blockCountRaw === 'number' && Number.isFinite(blockCountRaw) && blockCountRaw > 0
      ? Math.floor(blockCountRaw)
      : 1;
  const naming = typeof flow.blockNamingScheme === 'string' ? flow.blockNamingScheme : '';
  if (blockCount <= 1) return ['Block 1'];
  if (naming === 'alphabetic') {
    return Array.from({ length: blockCount }, (_, i) => `Block ${String.fromCharCode(65 + i)}`);
  }
  if (naming === 'custom' && typeof flow.customBlockNames === 'string') {
    const names = flow.customBlockNames
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);
    if (names.length >= blockCount) return names.slice(0, blockCount);
  }
  return Array.from({ length: blockCount }, (_, i) => `Block ${i + 1}`);
}

function getUnitLabel(
  floor: number,
  unitIndex: number,
  flow: Record<string, unknown>
): string {
  const pattern = typeof flow.doorPattern === 'string' ? flow.doorPattern : '';
  if (pattern === '1A-1B-1C') return `${floor}${String.fromCharCode(64 + unitIndex)}`;
  if (pattern === '11-12-13') return `${floor}${unitIndex}`;
  if (pattern === '101-102-103') return `${floor}${String(unitIndex).padStart(2, '0')}`;
  return `${floor}${String(unitIndex).padStart(2, '0')}`;
}

function getRouteLabel(pathname: string): string {
  if (pathname === '/onboarding' || pathname === '/') return 'Onboarding';
  if (pathname === '/dashboard') return 'Dashboard';
  if (pathname.startsWith('/resources/')) return 'Resources';
  if (pathname.startsWith('/community/')) return 'Community';
  if (pathname.startsWith('/employee/')) return 'Employee';
  if (pathname.startsWith('/finance/')) return 'Finance';
  if (pathname.startsWith('/residents/')) return 'Residents';
  if (pathname === '/documents') return 'Documents';
  if (pathname === '/notifications') return 'Notifications';
  if (pathname === '/workspace') return 'Workspace';
  if (pathname === '/settings') return 'Settings';
  if (pathname === '/login') return 'Login';
  if (pathname === '/register') return 'Register';
  return 'My Apartment App';
}

export const CopilotAssistant: React.FC = () => {
  const { message: messageApi } = App.useApp();
  const location = useLocation();
  const navigate = useNavigate();
  const { firestoreUser } = useAuth();
  const normalizeCopilotLang = (lng: string | undefined): "en" | "es" | "pt" => {
    const v = (lng || "").toLowerCase();
    if (v.startsWith("es")) return "es";
    if (v.startsWith("pt")) return "pt";
    return "en";
  };
  const [copilotLang, setCopilotLang] = useState<"en" | "es" | "pt">(
    normalizeCopilotLang(i18n.language)
  );
  useEffect(() => {
    const handler = (lng: string) => setCopilotLang(normalizeCopilotLang(lng));
    i18n.on("languageChanged", handler);
    return () => {
      i18n.off("languageChanged", handler);
    };
  }, []);
  const {
    onboardingCtx,
    setOnboardingCtx,
    flowData,
    setFlowData,
    setStepSelectionRequest,
  } = useOnboardingFlow();

  const routeLabel = useMemo(
    () => getRouteLabel(location.pathname),
    [location.pathname]
  );

  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [input, setInput] = useState('');
  const [inlineInputs, setInlineInputs] = useState<Record<string, string>>({});

  const chatBodyRef = useRef<HTMLDivElement | null>(null);

  const userName = firestoreUser?.displayName?.trim() || 'there';
  const workspaceName =
    firestoreUser?.workspace?.name?.trim() ||
    firestoreUser?.pendingWorkspace?.apartmentName?.trim() ||
    '';

  const ui = useMemo(() => {
    if (copilotLang === "es") {
      return {
        assistantLabel: "Asistente",
        youLabel: "Tú",
        modalTitle: "Asistente",
        modalSubtitle: "Mi aplicación · guía paso a paso",
        tooltip: "Asistente",
        placeholder: "Pregúntame cualquier cosa…",
        send: "Enviar",
        submit: "Enviar",
        clearChat: "Borrar chat",
        close: "Cerrar",
        buildingPreview: "Vista previa del edificio",
        currentStepLabel: "Paso actual",
        greetingWithWorkspace: (workspace: string) =>
          `Hola ${userName}, ¿cómo puedo ayudarte con la configuración de "${workspace}" hoy?`,
        greetingWithoutWorkspace: () =>
          `Hola ${userName}, ¿cómo puedo ayudarte con la configuración de tu apartamento hoy?`,
      };
    }
    if (copilotLang === "pt") {
      return {
        assistantLabel: "Assistente",
        youLabel: "Você",
        modalTitle: "Assistente",
        modalSubtitle: "Meu aplicativo · guia passo a passo",
        tooltip: "Assistente",
        placeholder: "Pergunte-me qualquer coisa…",
        send: "Enviar",
        submit: "Enviar",
        clearChat: "Limpar chat",
        close: "Fechar",
        buildingPreview: "Pré-visualização do edifício",
        currentStepLabel: "Etapa atual",
        greetingWithWorkspace: (workspace: string) =>
          `Olá ${userName}, como posso ajudar com a configuração de "${workspace}" hoje?`,
        greetingWithoutWorkspace: () =>
          `Olá ${userName}, como posso ajudar com a configuração do seu apartamento hoje?`,
      };
    }
    return {
      assistantLabel: "Assistant",
      youLabel: "You",
      modalTitle: "Assistant",
      modalSubtitle: "My Apartment App · step-by-step guidance",
      tooltip: "Assistant",
      placeholder: "Ask anything…",
      send: "Send",
      submit: "Submit",
      clearChat: "Clear chat",
      close: "Close",
      buildingPreview: "Building preview",
      currentStepLabel: "Current step",
      greetingWithWorkspace: (workspace: string) =>
        `Hey ${userName}, how can I assist you with "${workspace}" setup today?`,
      greetingWithoutWorkspace: () =>
        `Hey ${userName}, how can I assist you with your apartment setup today?`,
    };
  }, [copilotLang, userName]);

  const greeting = workspaceName
    ? ui.greetingWithWorkspace(workspaceName)
    : ui.greetingWithoutWorkspace();

  const floorPlanPreviewData = useMemo(() => {
    const floorsRaw = flowData.defaultFloors;
    const unitsRaw = flowData.defaultUnitsPerFloor;
    const floors =
      typeof floorsRaw === 'number' && Number.isFinite(floorsRaw) ? Math.floor(floorsRaw) : 0;
    const units =
      typeof unitsRaw === 'number' && Number.isFinite(unitsRaw) ? Math.floor(unitsRaw) : 0;
    if (floors <= 0 || units <= 0) return null;
    return {
      blockNames: buildPreviewBlockNames(flowData),
      floors,
      units,
    };
  }, [flowData]);

  const activeCtx = onboardingCtx;

  const [messages, setMessages] = useState<CopilotMessage[]>(() => {
    const restored = loadMessages();
    if (restored.length > 0) return restored;
    return [
      {
        id: 'init',
        role: 'assistant',
        ts: Date.now(),
        content:
          activeCtx
            ? `${greeting}\n\n${ui.currentStepLabel}: ${activeCtx.stepLabel} (${activeCtx.progress}%).`
            : greeting,
      },
    ];
  });

  useEffect(() => {
    // Keep the client-side init greeting consistent with the current UI language.
    setMessages((prev) => {
      const idx = prev.findIndex((m) => m.id === 'init');
      if (idx < 0) return prev;
      const nextContent = activeCtx
        ? `${greeting}\n\n${ui.currentStepLabel}: ${activeCtx.stepLabel} (${activeCtx.progress}%).`
        : greeting;
      const next = [...prev];
      next[idx] = { ...next[idx], content: nextContent };
      return next;
    });
  }, [greeting, ui.currentStepLabel, activeCtx?.stepLabel, activeCtx?.progress]);

  useEffect(() => {
    saveMessages(messages);
  }, [messages]);

  useEffect(() => {
    if (!chatBodyRef.current) return;
    chatBodyRef.current.scrollTo({
      top: chatBodyRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [messages, open]);

  const sendTextToCopilot = async (
    text: string,
    contextOverride?: StoredOnboardingContext,
    firstGreetingNeeded?: boolean,
    flowDataOverride?: Record<string, unknown>
  ) => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;

    const userMsg: CopilotMessage = {
      id: `u-${Date.now()}`,
      role: 'user',
      content: trimmed,
      ts: Date.now(),
    };
    setMessages((prev) => [...prev, userMsg].slice(-MAX_LOCAL_MESSAGES));
    setSending(true);

    try {
      const historyForApi = [...messages, userMsg]
        .filter((m) => m.role === 'user' || m.role === 'assistant')
        .slice(-20)
        .map((m) => ({ role: m.role, content: m.content }));
      const ctx = contextOverride || activeCtx;
      const { reply, options, inputRequests } = await callOnboardingCopilot({
        messages: historyForApi,
        context: {
          step: ctx ? ctx.step : `route:${location.pathname}`,
          progress: ctx ? ctx.progress : 0,
          stepLabel: ctx ? ctx.stepLabel : routeLabel,
          stepPrompt: ctx ? ctx.stepPrompt : '',
          userName: firestoreUser?.displayName || '',
          workspaceLanguage:
            (flowDataOverride?.language as 'en' | 'es' | 'pt' | undefined) ||
            (firestoreUser?.workspace?.language as 'en' | 'es' | 'pt' | undefined) ||
            (i18n.language as 'en' | 'es' | 'pt' | undefined) ||
            'en',
          workspaceName:
            firestoreUser?.workspace?.name ||
            firestoreUser?.pendingWorkspace?.apartmentName ||
            '',
          flowData: flowDataOverride || flowData,
          firstGreetingNeeded:
            typeof firstGreetingNeeded === 'boolean'
              ? firstGreetingNeeded
              : !messages.some((m) => m.role === 'assistant'),
        },
      });

      setMessages((prev) => [
        ...prev,
        {
          id: `a-${Date.now()}`,
          role: 'assistant' as const,
          content: !messages.some((m) => m.role === 'assistant')
            ? reply
            : reply.replace(
                new RegExp(`^${greeting.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*`, 'i'),
                ''
              ).trim(),
          ts: Date.now(),
          options,
          inputRequests,
        },
      ].slice(-MAX_LOCAL_MESSAGES));
    } catch (err) {
      const errMsg =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message?: string }).message)
          : 'Request failed';
      messageApi.error(errMsg, 8);
      setMessages((prev) => prev.filter((m) => m.id !== userMsg.id));
      throw err;
    } finally {
      setSending(false);
    }
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    try {
      await sendTextToCopilot(text);
    } catch {
      setInput(text);
    }
  };

  const handleSelectOnboardingStep = async (option: OnboardingCopilotOption) => {
    const shouldNavigateToOnboarding = option.payload?.openSummary === true;
    const flowBlockCount =
      typeof flowData.blockCount === 'number' && Number.isFinite(flowData.blockCount)
        ? String(Math.floor(flowData.blockCount))
        : undefined;
    const normalizedFlowBlockCount =
      flowBlockCount === '1' ||
      flowBlockCount === '2' ||
      flowBlockCount === '3' ||
      flowBlockCount === '5+' ||
      flowBlockCount === '10+'
        ? flowBlockCount
        : undefined;
    const flowFloors =
      typeof flowData.defaultFloors === 'number' && Number.isFinite(flowData.defaultFloors)
        ? Math.floor(flowData.defaultFloors)
        : undefined;
    const flowUnitsPerFloor =
      typeof flowData.defaultUnitsPerFloor === 'number' &&
      Number.isFinite(flowData.defaultUnitsPerFloor)
        ? Math.floor(flowData.defaultUnitsPerFloor)
        : undefined;
    const nextStep =
      option.action === 'set_blocks_count' &&
      (option.payload?.blockCount === '1' ||
        option.payload?.blockCount === '2' ||
        option.payload?.blockCount === '3')
        ? 'individual-blocks'
        : option.payload?.step || activeCtx?.step || `route:${location.pathname}`;

    const projectedCtx: StoredOnboardingContext = {
      step: nextStep,
      progress: activeCtx?.progress || 0,
      stepLabel: STEP_LABEL[nextStep] || activeCtx?.stepLabel || routeLabel,
      stepPrompt: STEP_PROMPT[nextStep] || activeCtx?.stepPrompt || '',
      updatedAt: Date.now(),
    };

    const payload = {
      step: option.payload?.step,
      blockCount: option.payload?.blockCount || normalizedFlowBlockCount,
      floors:
        typeof option.payload?.floors === 'number' ? option.payload.floors : flowFloors,
      unitsPerFloor:
        typeof option.payload?.unitsPerFloor === 'number' ?
          option.payload.unitsPerFloor :
          flowUnitsPerFloor,
      openSummary: option.payload?.openSummary,
      ts: Date.now(),
    };
    const nextFlow = { ...flowData };
    if (option.payload?.key) {
      nextFlow[option.payload.key] = option.payload.value;
    }
    if (option.payload?.blockCount) {
      const num = Number(option.payload.blockCount.replace('+', ''));
      if (Number.isFinite(num)) nextFlow.blockCount = num;
    }
    if (typeof option.payload?.floors === 'number') {
      nextFlow.defaultFloors = option.payload.floors;
    }
    if (typeof option.payload?.unitsPerFloor === 'number') {
      nextFlow.defaultUnitsPerFloor = option.payload.unitsPerFloor;
    }
    setFlowData(nextFlow);
    setStepSelectionRequest(payload);
    if (shouldNavigateToOnboarding) {
      setOpen(false);
      navigate('/onboarding');
    }
    setOnboardingCtx(projectedCtx);

    try {
      await sendTextToCopilot(option.label, projectedCtx, false, nextFlow);
    } catch {
      // errors already surfaced in sendTextToCopilot
    }
  };

  const handleInlineInputSubmit = async (
    req: OnboardingCopilotInputRequest,
    value: string
  ) => {
    const v = value.trim();
    if (!v) return;
    const key = req.fieldKey.includes('.') ?
      req.fieldKey.split('.').pop() || req.fieldKey :
      req.fieldKey;
    const parsedValue =
      req.type === 'number' ? Number(v) : v;
    const nextFlow = {
      ...flowData,
      [key]: parsedValue,
    };
    setFlowData(nextFlow);
    setInlineInputs((prev) => ({ ...prev, [req.id]: '' }));
    await sendTextToCopilot(`${req.label}: ${v}`, undefined, undefined, nextFlow);
  };

  const handleClear = () => {
    const init: CopilotMessage = {
      id: `init-${Date.now()}`,
      role: 'assistant',
      ts: Date.now(),
      content:
        greeting,
    };
    setFlowData({});
    setMessages([init]);
  };

  return (
    <>
      <FloatButton
        icon={<RobotOutlined />}
        type="primary"
        tooltip={ui.tooltip}
        onClick={() => setOpen(true)}
        style={{ right: 24, bottom: 24 }}
      />

      <Modal
        open={open}
        onCancel={() => setOpen(false)}
        title={
          <Space align="center">
            <Avatar icon={<RobotOutlined />} style={{ background: '#1677ff' }} />
            <div>
              <Text strong>{ui.modalTitle}</Text>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {ui.modalSubtitle}
                </Text>
              </div>
            </div>
            <Tag color="blue">{routeLabel}</Tag>
          </Space>
        }
        footer={null}
        width={520}
        styles={{ body: { paddingTop: 8 } }}
        destroyOnClose={false}
      >
        <div
          ref={chatBodyRef}
          style={{
            maxHeight: 420,
            overflowY: 'auto',
            padding: 12,
            border: '1px solid #f0f0f0',
            borderRadius: 12,
            marginBottom: 12,
            background: 'linear-gradient(180deg, #fcfcff 0%, #f8fbff 100%)',
          }}
        >
          {messages.map((m) => (
            <div
              key={m.id}
              style={{
                marginBottom: 12,
                display: 'flex',
                justifyContent: m.role === 'assistant' ? 'flex-start' : 'flex-end',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  gap: 8,
                  maxWidth: '86%',
                  flexDirection: m.role === 'assistant' ? 'row' : 'row-reverse',
                  alignItems: 'flex-end',
                }}
              >
                <Avatar
                  size="small"
                  icon={m.role === 'assistant' ? <RobotOutlined /> : <UserOutlined />}
                  style={{
                    background: m.role === 'assistant' ? '#1677ff' : '#14b8a6',
                    flexShrink: 0,
                  }}
                />
                <div
                  style={{
                    padding: '10px 12px',
                    borderRadius: 12,
                    background: m.role === 'assistant' ? '#ffffff' : '#e6fffb',
                    border:
                      m.role === 'assistant'
                        ? '1px solid #e6f4ff'
                        : '1px solid #b5f5ec',
                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
                  }}
                >
                  <Text strong style={{ fontSize: 12, color: '#595959' }}>
                    {m.role === 'assistant' ? ui.assistantLabel : ui.youLabel}
                  </Text>
                  <Paragraph style={{ margin: '4px 0 0 0', whiteSpace: 'pre-wrap' }}>
                    {m.content}
                  </Paragraph>
                  {m.role === 'assistant' &&
                  floorPlanPreviewData &&
                  (m.content.includes('Floor plan summary:') ||
                    m.content.includes('Floor plan milestone ready.')) ? (
                    <div
                      style={{
                        marginTop: 10,
                        border: '1px solid #d9d9d9',
                        borderRadius: 8,
                        background: '#fafafa',
                        padding: 8,
                      }}
                    >
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {ui.buildingPreview} ({floorPlanPreviewData.blockNames.length} block(s))
                        </Text>
                      <div style={{ marginTop: 8, display: 'grid', gap: 8 }}>
                        {floorPlanPreviewData.blockNames.map((blockName) => (
                          <div
                            key={`${m.id}-${blockName}`}
                            style={{
                              border: '1px solid #e8e8e8',
                              borderRadius: 6,
                              background: '#fff',
                              padding: 6,
                            }}
                          >
                            <Text strong style={{ fontSize: 12 }}>
                              {blockName}
                            </Text>
                            <div style={{ marginTop: 6, display: 'grid', gap: 6 }}>
                              {Array.from(
                                { length: floorPlanPreviewData.floors },
                                (_, floorIndex) => {
                                  const floor = floorPlanPreviewData.floors - floorIndex;
                                  return (
                                    <div
                                      key={`${blockName}-f-${floor}`}
                                      style={{
                                        border: '1px solid #f0f0f0',
                                        borderRadius: 6,
                                        padding: 6,
                                        background: '#fcfcfc',
                                      }}
                                    >
                                      <Text type="secondary" style={{ fontSize: 11 }}>
                                        Floor {floor}
                                      </Text>
                                      <div
                                        style={{
                                          marginTop: 4,
                                          display: 'grid',
                                          gridTemplateColumns: `repeat(${Math.min(
                                            floorPlanPreviewData.units,
                                            6
                                          )}, minmax(0, 1fr))`,
                                          gap: 4,
                                        }}
                                      >
                                        {Array.from(
                                          { length: floorPlanPreviewData.units },
                                          (_, unitIndex) => (
                                            <div
                                              key={`${blockName}-${floor}-${unitIndex + 1}`}
                                              style={{
                                                border: '1px solid #d9d9d9',
                                                borderRadius: 4,
                                                textAlign: 'center',
                                                padding: '4px 2px',
                                                fontSize: 10,
                                                background: '#f0f0f0',
                                              }}
                                            >
                                              {getUnitLabel(floor, unitIndex + 1, flowData)}
                                            </div>
                                          )
                                        )}
                                      </div>
                                    </div>
                                  );
                                }
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                  {m.role === 'assistant' && Array.isArray(m.options) && m.options.length > 0 ? (
                    <Space
                      direction="vertical"
                      size={8}
                      style={{ width: '100%', marginTop: 10 }}
                    >
                      {m.options.map((opt, idx) => (
                        <Button
                          key={`${m.id}-${opt.id}-${idx}`}
                          size="small"
                          onClick={() => handleSelectOnboardingStep(opt)}
                          style={{
                            borderRadius: 10,
                            width: '100%',
                            textAlign: 'left',
                            justifyContent: 'flex-start',
                          }}
                        >
                          {opt.label}
                        </Button>
                      ))}
                    </Space>
                  ) : null}
                  {m.role === 'assistant' &&
                  Array.isArray(m.inputRequests) &&
                  m.inputRequests.length > 0 ? (
                    <Space
                      direction="vertical"
                      size={8}
                      style={{ width: '100%', marginTop: 10 }}
                    >
                      {m.inputRequests.map((req) => (
                        <div key={`${m.id}-${req.id}`}>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {req.label}
                          </Text>
                          {req.type === 'enum' && Array.isArray(req.options) ? (
                            <Space
                              direction="vertical"
                              size={6}
                              style={{ width: '100%', marginTop: 6 }}
                            >
                              {req.options.map((opt) => (
                                <Button
                                  key={`${req.id}-${opt}`}
                                  size="small"
                                  onClick={() => void handleInlineInputSubmit(req, opt)}
                                  style={{
                                    borderRadius: 10,
                                    width: '100%',
                                    textAlign: 'left',
                                    justifyContent: 'flex-start',
                                  }}
                                >
                                  {opt}
                                </Button>
                              ))}
                            </Space>
                          ) : (
                            <Space.Compact style={{ width: '100%', marginTop: 6 }}>
                              {req.type === 'number' ? (
                                <InputNumber
                                  min={req.min}
                                  max={req.max}
                                  value={Number(inlineInputs[req.id] || '') || undefined}
                                  onChange={(v) =>
                                    setInlineInputs((prev) => ({
                                      ...prev,
                                      [req.id]: v != null ? String(v) : '',
                                    }))
                                  }
                                  placeholder={req.placeholder}
                                  style={{ width: '100%' }}
                                />
                              ) : (
                                <Input
                                  value={inlineInputs[req.id] || ''}
                                  onChange={(e) =>
                                    setInlineInputs((prev) => ({
                                      ...prev,
                                      [req.id]: e.target.value,
                                    }))
                                  }
                                  placeholder={req.placeholder}
                                />
                              )}
                              <Button
                                size="small"
                                onClick={() =>
                                  void handleInlineInputSubmit(req, inlineInputs[req.id] || '')
                                }
                              >
                                {ui.submit}
                              </Button>
                            </Space.Compact>
                          )}
                        </div>
                      ))}
                    </Space>
                  ) : null}
                </div>
              </div>
            </div>
          ))}
        </div>

        <Space.Compact style={{ width: '100%', marginBottom: 10 }}>
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onPressEnter={() => void handleSend()}
            placeholder={ui.placeholder}
            style={{ borderRadius: '10px 0 0 10px' }}
          />
          <Button
            type="primary"
            icon={<SendOutlined />}
            loading={sending}
            disabled={sending}
            onClick={() => void handleSend()}
            style={{ borderRadius: '0 10px 10px 0' }}
          >
            {ui.send}
          </Button>
        </Space.Compact>

        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Button size="small" onClick={handleClear}>
            {ui.clearChat}
          </Button>
          <Button size="small" type="text" onClick={() => setOpen(false)}>
            {ui.close}
          </Button>
        </Space>
      </Modal>
    </>
  );
};

